import { StateCreator } from "zustand";
import {
  QAgentChat,
  QAgentChatPreview,
  CreateQAgentChatDto,
  getQAgentChats,
  getQAgentChat,
  createQAgentChat as createQAgentChatApi,
  deleteQAgentChat as deleteQAgentChatApi,
  attachProjectToChat,
} from "../services/QAgent";

export interface QAgentSlice {
  // State
  qagentChats: QAgentChatPreview[];
  activeQAgentChat: QAgentChat | null;
  isQAgentLoading: boolean;
  qagentError: string | null;

  // Actions
  fetchQAgentChats: () => Promise<void>;
  fetchQAgentChat: (chatId: string) => Promise<void>;
  createQAgentChat: (data?: CreateQAgentChatDto) => Promise<QAgentChat | null>;
  deleteQAgentChat: (chatId: string) => Promise<boolean>;
  attachProject: (chatId: string, projectId: string | null) => Promise<QAgentChat | null>;
  setActiveQAgentChat: (chat: QAgentChat | null) => void;
  updateQAgentChatTitle: (chatId: string, title: string) => void;
  updateQAgentChatProject: (chatId: string, projectId: string | null, projectName?: string | null) => void;
  clearQAgentError: () => void;
}

export const createQAgentSlice: StateCreator<QAgentSlice> = (set, get) => ({
  // Initial state
  qagentChats: [],
  activeQAgentChat: null,
  isQAgentLoading: false,
  qagentError: null,

  // Fetch all chats
  fetchQAgentChats: async () => {
    set({ isQAgentLoading: true, qagentError: null });
    try {
      const response = await getQAgentChats();
      if (response.success && response.data) {
        set({ qagentChats: response.data, isQAgentLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch chats";
        set({ qagentError: errorMsg, isQAgentLoading: false });
      }
    } catch (error) {
      set({ qagentError: "An unexpected error occurred", isQAgentLoading: false });
    }
  },

  // Fetch a single chat
  fetchQAgentChat: async (chatId: string) => {
    set({ isQAgentLoading: true, qagentError: null });
    try {
      const response = await getQAgentChat(chatId);
      if (response.success && response.data) {
        set({ activeQAgentChat: response.data, isQAgentLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch chat";
        set({ qagentError: errorMsg, isQAgentLoading: false });
      }
    } catch (error) {
      set({ qagentError: "An unexpected error occurred", isQAgentLoading: false });
    }
  },

  // Create a new chat
  createQAgentChat: async (data?: CreateQAgentChatDto) => {
    set({ isQAgentLoading: true, qagentError: null });
    try {
      const response = await createQAgentChatApi(data);
      if (response.success && response.data) {
        set((state) => ({
          qagentChats: [
            {
              ...response.data!,
              messageCount: 0,
              lastMessage: undefined,
            } as QAgentChatPreview,
            ...state.qagentChats,
          ],
          activeQAgentChat: response.data,
          isQAgentLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to create chat";
        set({ qagentError: errorMsg, isQAgentLoading: false });
        return null;
      }
    } catch (error) {
      set({ qagentError: "An unexpected error occurred", isQAgentLoading: false });
      return null;
    }
  },

  // Delete a chat
  deleteQAgentChat: async (chatId: string) => {
    set({ qagentError: null });
    try {
      const response = await deleteQAgentChatApi(chatId);
      if (response.success) {
        set((state) => ({
          qagentChats: state.qagentChats.filter((c) => c.id !== chatId),
          activeQAgentChat:
            state.activeQAgentChat?.id === chatId ? null : state.activeQAgentChat,
        }));
        return true;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to delete chat";
        set({ qagentError: errorMsg });
        return false;
      }
    } catch (error) {
      set({ qagentError: "An unexpected error occurred" });
      return false;
    }
  },

  // Attach/detach project from chat
  attachProject: async (chatId: string, projectId: string | null) => {
    set({ qagentError: null });
    try {
      const response = await attachProjectToChat(chatId, projectId);
      if (response.success && response.data) {
        set((state) => ({
          activeQAgentChat:
            state.activeQAgentChat?.id === chatId
              ? response.data!
              : state.activeQAgentChat,
          qagentChats: state.qagentChats.map((c) =>
            c.id === chatId ? { ...c, projectId: response.data!.projectId } : c
          ),
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to attach project";
        set({ qagentError: errorMsg });
        return null;
      }
    } catch (error) {
      set({ qagentError: "An unexpected error occurred" });
      return null;
    }
  },

  // Set active chat
  setActiveQAgentChat: (chat: QAgentChat | null) => {
    set({ activeQAgentChat: chat });
  },

  // Update a chat's title in the list
  updateQAgentChatTitle: (chatId: string, title: string) => {
    set((state) => ({
      qagentChats: state.qagentChats.map((c) =>
        c.id === chatId ? { ...c, title } : c
      ),
    }));
  },

  // Update a chat's project in the list (e.g. after one was created for it)
  updateQAgentChatProject: (chatId: string, projectId: string | null, projectName?: string | null) => {
    set((state) => ({
      qagentChats: state.qagentChats.map((c) =>
        c.id === chatId
          ? { ...c, projectId, projectName: projectName ?? undefined }
          : c
      ),
    }));
  },

  // Clear error
  clearQAgentError: () => {
    set({ qagentError: null });
  },
});
